import { CustomHeaderProps } from 'ag-grid-react';
import { ColDef, IRowNode } from 'ag-grid-community';
import styled, { createGlobalStyle } from '@benzinga/themetron';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';

import { faArrowUp, faArrowDown, faArrowsUpDown } from '@fortawesome/pro-solid-svg-icons';
import { noop } from '@benzinga/utils';

export interface MyCustomHeaderProps extends CustomHeaderProps {
  menuIcon: string;
}

type AbsSortTypes = 'asc' | 'desc' | 'desc-abs' | null;

const absCompare = (comparator: AdvancedColDef['comparator']) =>
  comparator
    ? (valueA: any, valueB: any, nodeA: IRowNode<any>, nodeB: IRowNode<any>, isDescending: boolean) =>
        comparator(Math.abs(valueA), Math.abs(valueB), nodeA, nodeB, isDescending)
    : (valueA: any, valueB: any) => (valueA === valueB ? 0 : Math.abs(valueA) > Math.abs(valueB) ? 1 : -1);

export const absColDef = (colDef: AdvancedColDef): AdvancedColDefInternal => ({
  advancedSort: {
    absSort: false,
    localSort: true,
    onSetSort: noop,
    val: null,
    ...((colDef.advancedSort as object) ?? {}),
    comparator: colDef.comparator,
  },
  comparator: colDef.advancedSort?.val === 'desc-abs' ? absCompare(colDef.comparator) : colDef.comparator,
  headerClass: 'bz-ag-grid-header',
  headerComponent: AbsColHeader,
  sort: null,
});

export type AdvancedColDef = ColDef & {
  advancedSort?:
    | {
        absSort: true;
        localSort?: boolean;
        onSetSort?: (sort: 'asc' | 'desc' | 'desc-abs' | null) => void;
        val: 'asc' | 'desc' | 'desc-abs' | null;
      }
    | {
        absSort: false;
        localSort?: boolean;
        onSetSort?: (sort: 'asc' | 'desc' | null) => void;
        val: 'asc' | 'desc' | null;
      };
};

type AdvancedColDefInternal = AdvancedColDef & {
  advancedSort?: AdvancedColDef['advancedSort'] & {
    comparator: ColDef['comparator'];
  };
};

export const AbsColHeader = (props: MyCustomHeaderProps) => {
  const getSortValue = React.useCallback(() => {
    const colDef = props.column.getColDef() as AdvancedColDef;
    if (colDef.advancedSort) {
      return colDef.advancedSort.val;
    } else {
      return props.column.getSort() ?? null;
    }
  }, [props.column]);

  const [sort, setSort] = useState<AbsSortTypes>(() => getSortValue());

  const refButton = useRef(null);

  const onMenuClicked = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(
    event => {
      event.stopPropagation();
      props.showColumnMenu(refButton.current!);
    },
    [props],
  );

  const onSortClicked = React.useCallback(
    (event: any) => {
      const colDef = props.column.getColDef() as AdvancedColDefInternal;
      if (colDef.advancedSort) {
        const newSort = (() => {
          if (colDef.advancedSort.absSort) {
            return sort === 'asc' ? 'desc' : sort === 'desc' ? 'desc-abs' : sort === 'desc-abs' ? null : 'asc';
          } else {
            return sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
          }
        })();
        // sets the sort for all abs columns
        props.api.getColumns()?.forEach(col => {
          const colDef2 = col.getColDef() as AdvancedColDefInternal;

          if (col.getColId() === props.column.getColId()) {
            if (colDef2.advancedSort) {
              colDef2.advancedSort.val = newSort;
              colDef2.comparator =
                newSort === 'desc-abs'
                  ? absCompare(colDef2.advancedSort?.comparator)
                  : colDef2.advancedSort?.comparator;
            }
          } else if (colDef2.advancedSort?.val !== null) {
            if (colDef2.advancedSort) {
              colDef2.advancedSort.val = null;
              colDef2.comparator = colDef2.advancedSort?.comparator;
            }
          }
        });

        // sets the sort for all non-abs columns

        colDef.advancedSort.localSort && props.setSort(newSort === 'desc-abs' ? 'desc' : newSort, event.shiftKey);
        if (colDef.advancedSort?.onSetSort) {
          colDef.advancedSort.onSetSort(newSort as any);
        }
        setSort(newSort);
      } else {
        const newSort = sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
        props.setSort(newSort as any, event.shiftKey);
        setSort(newSort as any);
      }
    },
    [props, sort],
  );

  useEffect(() => {
    const myOnSortChanged = () => {
      const newSort = getSortValue();
      setSort(newSort);
    };
    props.column.addEventListener('sortChanged', myOnSortChanged);
    props.column.addEventListener('colDefChanged', myOnSortChanged);
    return () => {
      props.column.removeEventListener('sortChanged', myOnSortChanged);
      props.column.removeEventListener('colDefChanged', myOnSortChanged);
    };
  }, [getSortValue, props.api, props.column]);

  return (
    <>
      <Global />
      <Container
        className={
          `${sort ? `bz-ag-grid-header-${sort}` : ''} ${props.enableSorting ? `.bz-ag-grid-header-sort` : ''}` ||
          undefined
        }
        onClick={onSortClicked}
      >
        <span className="ag-header-icon ag-sort-order" data-ref="eSortOrder" />
        <Left>
          <Elipces>{props.displayName}</Elipces>
          {sort === 'asc' && <FontAwesomeIcon icon={faArrowUp} />}
          {sort === 'desc' && <FontAwesomeIcon icon={faArrowDown} />}
          {sort === 'desc-abs' && <FontAwesomeIcon icon={faArrowsUpDown} />}
        </Left>
        {props.enableMenu && (
          <div className={'menu'} onClick={onMenuClicked} ref={refButton}>
            <span aria-hidden="true" className="ag-header-icon ag-header-cell-menu-button">
              <span className="ag-icon ag-icon-menu" role="presentation" unselectable="on"></span>
            </span>
          </div>
        )}
      </Container>
    </>
  );
};

const Elipces = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  height: 16px;
`;

const Global = createGlobalStyle`
  .ag-header-cell-sortable {
    cursor: pointer;
  }

  .bz-ag-grid-header .menu {
    display: none;
  }

  .bz-ag-grid-header:hover {
    .menu {
      display: block;
    }
  }

  .bz-ag-grid-header:has(.bz-ag-grid-header-desc) {
    background: #11463b !important;
  }

  .bz-ag-grid-header:has(.bz-ag-grid-header-asc) {
    background: #4b1c19 !important;
  }

  .bz-ag-grid-header:has(.bz-ag-grid-header-desc-abs) {
    background: #141742 !important;
  }

`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const Left = styled.div`
  flex-grow: 2;
  text-overflow: ellipsis;
  overflow: hidden;
  display: flex;
  gap: 4px;
`;
